import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  VStack,
  Heading,
  Card,
  Text,
  HStack,
  Progress,
  SimpleGrid,
  Table,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import { Field } from "@chakra-ui/react";
import { useAppSelector } from "../store/hooks";
import {
  FiUpload,
  FiDatabase,
  FiRefreshCw,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiPlay,
} from "react-icons/fi";
import api from "../lib/api";
import { v4 as uuidv4 } from "uuid";
import { toaster } from "../components/ui/toaster";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

interface TransactionStats {
  total_transactions: number;
  total_revenue: number;
  total_cost: number;
  unique_products: number;
  unique_locations: number;
}

interface DataUpload {
  id: number;
  filename: string;
  uploaded_at: string;
  ingestion_status: string;
  total_rows: number;
  rows_ingested: number;
  retry_count: number;
  processing_started_at: string | null;
  ingested_at: string | null;
  processing_time_seconds: number | null;
  error_log: string | null;
  failure_reason: string | null;
  retry_guidance: string | null;
  validation_message: string | null;
}

interface SweepJob {
  id: number;
  source_label: string;
  triggered_by: number;
  started_at: string;
  status: string; // pending | processing | completed | failed
  sweep_from: string | null;
  sweep_until: string | null;
  rows_fetched: number;
  rows_ingested: number;
  rows_skipped: number;
  completed_at: string | null;
  error_log: string | null;
}

export default function DataUploadPage() {
  const { user, accessToken } = useAppSelector((state) => state.auth);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stats, setStats] = useState<TransactionStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [uploads, setUploads] = useState<DataUpload[]>([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);

  // Sweep state
  const [sweeps, setSweeps] = useState<SweepJob[]>([]);
  const [loadingSweeps, setLoadingSweeps] = useState(false);
  const [triggeringSweep, setTriggeringSweep] = useState(false);
  const sweepPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check if user is admin
  if (user?.role !== "admin") {
    return (
      <Box p={6}>
        <Card.Root maxW="2xl" mx="auto">
          <Card.Body p={8} textAlign="center">
            <Heading size="lg" color="red.600" mb={4}>
              Access Denied
            </Heading>
            <Text color="gray.600">
              This page is only accessible to administrators.
            </Text>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  }

  useEffect(() => {
    fetchStats();
    fetchUploads();
    fetchSweeps();
  }, []);

  // Auto-refresh sweeps while any is in progress
  useEffect(() => {
    const hasActive = sweeps.some(
      (s) => s.status === "pending" || s.status === "processing"
    );
    if (hasActive && !sweepPollRef.current) {
      sweepPollRef.current = setInterval(fetchSweeps, 5000);
    } else if (!hasActive && sweepPollRef.current) {
      clearInterval(sweepPollRef.current);
      sweepPollRef.current = null;
    }
    return () => {
      if (sweepPollRef.current) {
        clearInterval(sweepPollRef.current);
        sweepPollRef.current = null;
      }
    };
  }, [sweeps]);

  const fetchSweeps = async () => {
    setLoadingSweeps(true);
    try {
      const response = await api.get("/admin/sweeps?limit=20");
      setSweeps(response.data.data.sweeps);
    } catch (error) {
      console.error("Sweeps fetch error:", error);
    } finally {
      setLoadingSweeps(false);
    }
  };

  const handleTriggerSweep = async () => {
    setTriggeringSweep(true);
    try {
      const response = await api.post("/admin/sweep-external-db", {
        source_label: "Manual Sweep",
      });
      toaster.create({
        title: "Sweep started",
        description: `Sweep #${response.data.data.sweep_id} queued. It will run in the background.`,
        type: "success",
      });
      setTimeout(fetchSweeps, 1000);
    } catch (error: any) {
      toaster.create({
        title: "Sweep failed to start",
        description:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Could not reach source database",
        type: "error",
      });
    } finally {
      setTriggeringSweep(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toaster.create({
        title: "No file selected",
        description: "Please select a CSV file to upload",
        type: "warning",
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    const requestId = uuidv4();
    const formData = new FormData();
    formData.append("file", selectedFile);

    // Start listening for progress
    const eventSource = new EventSource(
      `${API_BASE_URL}/admin/progress/${requestId}?token=${accessToken}`,
    );

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setUploadProgress(data.progress);
      if (data.progress >= 100) {
        eventSource.close();
      }
    };

    eventSource.onerror = () => {
      eventSource.close();
    };

    try {
      const response = await api.post(
        `/admin/upload-transactions?request_id=${requestId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toaster.create({
        title: "Upload successful",
        description: `${response.data.data?.count || 0} transactions uploaded successfully`,
        type: "success",
      });

      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById(
        "file-upload",
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";

      // Refresh stats
      fetchStats();
      fetchUploads();
    } catch (error: any) {
      console.error("Upload error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to upload file";

      toaster.create({
        title: "Upload failed",
        description: errorMessage,
        type: "error",
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      eventSource.close();
    }
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await api.get("/admin/transactions/stats");
      // The API returns { data: { ...stats }, ... }
      // So we need response.data.data
      setStats(response.data.data);
    } catch (error: any) {
      console.error("Stats error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Could not fetch transaction statistics";
      toaster.create({
        title: "Failed to load stats",
        description: errorMessage,
        type: "error",
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchUploads = async () => {
    setLoadingUploads(true);
    try {
      const response = await api.get(`/admin/uploads`);
      setUploads(response.data.data.uploads);
    } catch (error: any) {
      console.error("Uploads error:", error);
      toaster.create({
        title: "Failed to load uploads",
        description: "Could not fetch upload history",
        type: "error",
      });
    } finally {
      setLoadingUploads(false);
    }
  };

  const handleRetry = async (uploadId: number) => {
    setRetryingId(uploadId);
    try {
      const response = await api.post(`/admin/uploads/${uploadId}/retry`);
      toaster.create({
        title: "Retry triggered",
        description: response.data.message || "Ingestion retry started",
        type: "success",
      });
      // Refresh uploads after a short delay
      setTimeout(() => {
        fetchUploads();
      }, 2000);
    } catch (error: any) {
      console.error("Retry error:", error);
      toaster.create({
        title: "Retry failed",
        description: error.response?.data?.message || "Could not retry upload",
        type: "error",
      });
    } finally {
      setRetryingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: "gray", icon: FiClock },
      processing: { color: "blue", icon: FiRefreshCw },
      completed: { color: "green", icon: FiCheckCircle },
      failed: { color: "red", icon: FiXCircle },
    };
    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge colorPalette={config.color}>
        <HStack gap={1}>
          <Icon size={12} />
          <span>{status}</span>
        </HStack>
      </Badge>
    );
  };

  const getSweepStatusBadge = (status: string) => getStatusBadge(status);

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "-";
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    // Amount is in Kobo, convert to Naira for display
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(amount / 100);
  };

  const filteredUploads = showIssuesOnly
    ? uploads.filter(
      (upload) =>
        upload.ingestion_status === "failed" ||
        !!upload.validation_message ||
        !!upload.failure_reason ||
        !!upload.retry_guidance,
    )
    : uploads;

  return (
    <Box p={6}>
      <Heading size="xl" mb={6}>
        Data Sync
      </Heading>

      <VStack align="stretch" gap={6}>
        {/* Upload Section — disabled; data is synced via Source DB sweep */}
        {false && (<Card.Root>
          <Card.Header>
            <HStack gap={2}>
              <FiUpload size={20} />
              <Heading size="lg">Upload Transaction Data</Heading>
            </HStack>
          </Card.Header>
          <Card.Body>
            <VStack align="stretch" gap={4}>
              <Field.Root>
                <Field.Label>Select CSV File</Field.Label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  style={{
                    padding: "8px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    width: "100%",
                  }}
                />
                <Field.HelperText>
                  Upload medication transaction data in CSV format
                </Field.HelperText>
              </Field.Root>

              {selectedFile && (
                <Box p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" color="blue.700">
                    <strong>Selected:</strong> {(selectedFile as File).name} (
                    {((selectedFile as File).size / 1024 / 1024).toFixed(2)} MB)
                  </Text>
                </Box>
              )}

              {uploading && (
                <Box w="100%">
                  <Text fontSize="sm" mb={1}>
                    Uploading and Processing: {uploadProgress}%
                  </Text>
                  <Progress.Root
                    value={uploadProgress}
                    size="sm"
                    colorScheme="blue">
                    <Progress.Track>
                      <Progress.Range />
                    </Progress.Track>
                  </Progress.Root>
                </Box>
              )}

              <HStack gap={3} wrap={"wrap"}>
                <Button
                  colorScheme="blue"
                  onClick={handleUpload}
                  loading={uploading}
                  disabled={!selectedFile}>
                  <FiUpload style={{ marginRight: "8px" }} />
                  {uploading ? "Uploading..." : "Upload Data"}
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={fetchStats}
                  loading={loadingStats}>
                  <FiDatabase style={{ marginRight: "8px" }} />
                  Refresh Stats
                </Button>
              </HStack>
            </VStack>
          </Card.Body>
        </Card.Root>)}

        {/* Statistics Section */}
        {stats && (
          <Card.Root>
            <Card.Header>
              <HStack justifyContent="space-between">
                <HStack gap={2}>
                  <FiDatabase size={20} />
                  <Heading size="lg">Transaction Statistics</Heading>
                </HStack>
                <Button size="sm" variant="outline" onClick={fetchStats} loading={loadingStats}>
                  <FiRefreshCw />
                  Refresh
                </Button>
              </HStack>
            </Card.Header>
            <Card.Body>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                <Box p={4} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Total Transactions
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.700">
                    {stats.total_transactions.toLocaleString()}
                  </Text>
                </Box>

                <Box p={4} bg="green.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Total Revenue
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.700">
                    {formatCurrency(stats.total_revenue)}
                  </Text>
                </Box>


                <Box p={4} bg="orange.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Total Cost
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.700">
                    {formatCurrency(stats.total_cost)}
                  </Text>
                </Box>

                <Box p={4} bg="cyan.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Unique Products
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="cyan.700">
                    {stats.unique_products.toLocaleString()}
                  </Text>
                </Box>

                <Box p={4} bg="pink.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Locations
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="pink.700">
                    {stats.unique_locations.toLocaleString()}
                  </Text>
                </Box>
              </SimpleGrid>
            </Card.Body>
          </Card.Root>
        )}

        {false && (<Card.Root bg="gray.50">
          <Card.Body>
            <VStack align="stretch" gap={2}>
              <Heading size="sm" color="gray.700">
                Instructions
              </Heading>
              <Text fontSize="sm" color="gray.600">
                1. Select a CSV file containing medication transaction data
              </Text>
              <Text fontSize="sm" color="gray.600">
                2. Ensure the CSV has the following columns: transaction_id,
                date, created_at, location_id, city, product_description,
                unit_type, quantity, retail_price, total_price, cost_price,
                current_stock, expiry_date, mode_of_transaction, barcode, ref_no
              </Text>
              <Text fontSize="sm" color="gray.600">
                3. Click "Upload Data" to import the transactions
              </Text>
              <Text fontSize="sm" color="gray.600">
                4. Use "Refresh Stats" to view updated statistics after upload
              </Text>
            </VStack>
          </Card.Body>
        </Card.Root>)}

        {/* Upload History Section — disabled */}
        {false && (<Card.Root>
          <Card.Header>
            <HStack justify="space-between">
              <HStack gap={2}>
                <FiDatabase size={20} />
                <Heading size="lg">Upload History</Heading>
              </HStack>
              <HStack gap={2}>
                <Button
                  size="sm"
                  variant={showIssuesOnly ? "solid" : "outline"}
                  colorScheme={showIssuesOnly ? "orange" : "gray"}
                  onClick={() => setShowIssuesOnly((prev) => !prev)}>
                  {showIssuesOnly ? "Showing Issues Only" : "Show Issues Only"}
                </Button>
                <Button
                  size="sm"
                  onClick={fetchUploads}
                  disabled={loadingUploads}>
                  <FiRefreshCw />
                  Refresh
                </Button>
              </HStack>
            </HStack>
          </Card.Header>
          <Card.Body>
            {loadingUploads ? (
              <Box textAlign="center" py={8}>
                <Spinner size="lg" color="blue.500" />
              </Box>
            ) : filteredUploads.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">
                  {showIssuesOnly
                    ? "No uploads with issues found"
                    : "No uploads found"}
                </Text>
              </Box>
            ) : (
              <Box overflowX="auto">
                <Table.Root variant="outline" size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Filename</Table.ColumnHeader>
                      <Table.ColumnHeader>Status</Table.ColumnHeader>
                      <Table.ColumnHeader>Uploaded</Table.ColumnHeader>
                      <Table.ColumnHeader>Rows</Table.ColumnHeader>
                      <Table.ColumnHeader>Processing Time</Table.ColumnHeader>
                      <Table.ColumnHeader>Retries</Table.ColumnHeader>
                      <Table.ColumnHeader>Completed</Table.ColumnHeader>
                      <Table.ColumnHeader>Failure Reason</Table.ColumnHeader>
                      <Table.ColumnHeader>Guidance</Table.ColumnHeader>
                      <Table.ColumnHeader>Actions</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {filteredUploads.map((upload) => (
                      <Table.Row key={upload.id}>
                        <Table.Cell>
                          <VStack align="start" gap={0}>
                            <Text fontWeight="medium">{upload.filename}</Text>
                            {upload.validation_message && (
                              <HStack gap={1} color="orange.600">
                                <FiAlertCircle size={12} />
                                <Text fontSize="xs">{upload.validation_message}</Text>
                              </HStack>
                            )}
                            {upload.ingestion_status === "failed" &&
                              upload.failure_reason && (
                                <HStack gap={1} color="red.500">
                                  <FiAlertCircle size={12} />
                                  <Text fontSize="xs">
                                    Failed upload
                                  </Text>
                                </HStack>
                              )}
                          </VStack>
                        </Table.Cell>
                        <Table.Cell>
                          {getStatusBadge(upload.ingestion_status)}
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs">
                            {formatDateTime(upload.uploaded_at)}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text>
                            {upload.rows_ingested.toLocaleString()} /{" "}
                            {upload.total_rows.toLocaleString()}
                          </Text>
                          {upload.total_rows > 0 && (
                            <Progress.Root
                              value={
                                (upload.rows_ingested / upload.total_rows) * 100
                              }
                              size="xs"
                              colorPalette={
                                upload.ingestion_status === "completed"
                                  ? "green"
                                  : "blue"
                              }
                              mt={1}>
                              <Progress.Track>
                                <Progress.Range />
                              </Progress.Track>
                            </Progress.Root>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {formatDuration(upload.processing_time_seconds)}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {upload.retry_count > 0 ? (
                            <Badge colorPalette="orange">
                              {upload.retry_count}
                            </Badge>
                          ) : (
                            <Text color="gray.400">-</Text>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs">
                            {formatDateTime(upload.ingested_at)}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          {upload.ingestion_status === "failed" ? (
                            <HStack align="start" gap={1} color="red.600">
                              <FiAlertCircle size={12} style={{ marginTop: "2px" }} />
                              <Text fontSize="xs" maxW="280px" lineClamp={3}>
                                {upload.failure_reason ||
                                  "Upload failed due to a processing error. Check logs for details."}
                              </Text>
                            </HStack>
                          ) : (
                            <Text color="gray.400">-</Text>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {upload.ingestion_status === "failed" ? (
                            <Text
                              fontSize="xs"
                              color={
                                upload.retry_guidance?.toLowerCase().includes("safe to retry")
                                  ? "blue.600"
                                  : "orange.600"
                              }
                              maxW="220px"
                              lineClamp={3}>
                              {upload.retry_guidance || "Review failure details before retrying."}
                            </Text>
                          ) : (
                            <Text color="gray.400">-</Text>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {(upload.ingestion_status === "failed" ||
                            upload.ingestion_status === "processing") && (
                              <Button
                                size="xs"
                                colorPalette="blue"
                                onClick={() => handleRetry(upload.id)}
                                disabled={retryingId === upload.id}>
                                {retryingId === upload.id ? (
                                  <>
                                    <Spinner size="xs" /> Retrying...
                                  </>
                                ) : (
                                  <>
                                    <FiRefreshCw /> Retry
                                  </>
                                )}
                              </Button>
                            )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </Card.Body>
        </Card.Root>)}
        {/* Source DB Sweep History */}
        <Card.Root>
          <Card.Header>
            <HStack justify="space-between">
              <HStack gap={2}>
                <FiDatabase size={20} />
                <Heading size="lg">Source DB Sweeps</Heading>
              </HStack>
              <HStack gap={2}>
                <Button
                  size="sm"
                  colorPalette="blue"
                  onClick={handleTriggerSweep}
                  loading={triggeringSweep}
                  disabled={sweeps.some(
                    (s) => s.status === "pending" || s.status === "processing"
                  )}>
                  <FiPlay />
                  Run Sweep Now
                </Button>
                <Button
                  size="sm"
                  onClick={fetchSweeps}
                  disabled={loadingSweeps}>
                  <FiRefreshCw />
                  Refresh
                </Button>
              </HStack>
            </HStack>
          </Card.Header>
          <Card.Body>
            {loadingSweeps && sweeps.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Spinner size="lg" color="blue.500" />
              </Box>
            ) : sweeps.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">No sweeps yet. Click "Run Sweep Now" to start the first one.</Text>
              </Box>
            ) : (
              <Box overflowX="auto">
                <Table.Root variant="outline" size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>#</Table.ColumnHeader>
                      <Table.ColumnHeader>Label</Table.ColumnHeader>
                      <Table.ColumnHeader>Status</Table.ColumnHeader>
                      <Table.ColumnHeader>Started</Table.ColumnHeader>
                      <Table.ColumnHeader>Fetched from Source</Table.ColumnHeader>
                      <Table.ColumnHeader>Rows</Table.ColumnHeader>
                      <Table.ColumnHeader>Completed</Table.ColumnHeader>
                      <Table.ColumnHeader>Error</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {sweeps.map((sweep) => (
                      <Table.Row key={sweep.id}>
                        <Table.Cell>{sweep.id}</Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm">{sweep.source_label}</Text>
                        </Table.Cell>
                        <Table.Cell>{getSweepStatusBadge(sweep.status)}</Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs">{formatDateTime(sweep.started_at)}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          {sweep.rows_fetched > 0 ? (
                            <VStack align="start" gap={0}>
                              <Text fontSize="xs" fontWeight="medium">
                                {sweep.rows_fetched.toLocaleString()} rows
                              </Text>
                              <Text fontSize="xs" color="gray.400">
                                ID-based sync
                              </Text>
                            </VStack>
                          ) : sweep.status === "completed" ? (
                            <Badge colorPalette="green" size="sm">
                              <FiCheckCircle size={10} />
                              Up to date
                            </Badge>
                          ) : (
                            <Text fontSize="xs" color="gray.400">—</Text>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <VStack align="start" gap={0}>
                            <Text fontSize="xs">
                              {sweep.rows_ingested.toLocaleString()} ingested
                            </Text>
                            {sweep.rows_skipped > 0 && (
                              <Text fontSize="xs" color="orange.600">
                                {sweep.rows_skipped.toLocaleString()} skipped (dupes)
                              </Text>
                            )}
                          </VStack>
                          {(sweep.status === "pending" || sweep.status === "processing") &&
                            sweep.rows_fetched > 0 && (
                              <Progress.Root
                                value={(sweep.rows_ingested / sweep.rows_fetched) * 100}
                                size="xs"
                                colorPalette="blue"
                                mt={1}>
                                <Progress.Track>
                                  <Progress.Range />
                                </Progress.Track>
                              </Progress.Root>
                            )}
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs">{formatDateTime(sweep.completed_at)}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          {sweep.error_log ? (
                            <HStack gap={1} color="red.600">
                              <FiAlertCircle size={12} />
                              <Text fontSize="xs" maxW="240px" lineClamp={2}>
                                {sweep.error_log}
                              </Text>
                            </HStack>
                          ) : (
                            <Text color="gray.400">—</Text>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  );
}
